import {Palette} from 'hubbl-shared';
import moment from 'moment';
import {memo, useCallback, useMemo, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import styled from 'styled-components';
import {useAppDispatch, useAppSelector, useI18n} from '../../hooks';
import {GlobalSelector} from '../../store/global/GlobalSelector';
import {HubblBreakDTO} from '../../models';
import {isUserInvited} from '../../utils';
import {GlobalActions} from '../../store/global/GlobalActions';
import {
  acceptBreak,
  declineBreak,
  undeclineBreak,
} from '../../store/hubbl/HubblThunks';
import {UserSelector} from '../../store/user/UserSelector';
import {AttendeeAvatar} from '../attendee-rack/AttendeeAvatar';
import {
  AnimatedWaitingDots,
  ConfirmationModal,
  DevToolFloatingId,
  SecondaryButton,
  TextHeading8,
  TextTicker,
} from '../index';
import {
  CardContainer,
  CardContent,
  CardDetails,
  CardFooter,
  CardStatusIndicator,
  CardTitle,
  formatEventDate,
  formatEventTime,
  StatusBadge,
  MOBILE_BREAKPOINT,
} from './shared';

const TitleWithAvatar = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const LearnMoreLink = styled.button`
  background: none;
  border: none;
  color: ${Palette.primaryTerraCotta};
  text-decoration: underline;
  cursor: pointer;
  font-size: inherit;
  font-family: inherit;
  padding: 0;
  margin: 0;

  &:hover {
    color: ${Palette.primaryPadua};
  }
`;

const ClickableName = styled.button`
  background: ${Palette.almostBlackTransparent5};
  border: 0px solid ${Palette.almostBlackTransparent10};
  border-radius: 6px;
  color: ${Palette.almostBlack};
  cursor: pointer;
  font-size: inherit;
  font-family: inherit;
  font-weight: 500;
  padding: 2px 6px;
  margin: 0;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  transition: all 0.2s ease;

  &:hover {
    background: ${Palette.almostBlackTransparent5};

    color: ${Palette.primaryTerraCotta};
    transform: translateY(-1px);
    box-shadow: 0 1px 4px ${Palette.almostBlackTransparent10};
  }

  &:active {
    transform: translateY(0);
  }
`;

interface HubblConnectCardProps {
  hubblConnect: HubblBreakDTO;
  isPast?: boolean;
}

export const HubblConnectCard = memo(
  ({hubblConnect, isPast = false}: HubblConnectCardProps) => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const userDetails = useAppSelector(UserSelector.getUserDetails);
    const t = useI18n();
    const currentLanguage = useAppSelector(GlobalSelector.getPreferredLanguage);
    const [isDeclineModalOpen, setIsDeclineModalOpen] = useState(false);

    const isUserCreator = useMemo(() => {
      if (!userDetails) return false;
      return hubblConnect.userCreator?.id === userDetails.id;
    }, [hubblConnect.userCreator, userDetails]);

    const isUserPartOfConnect = useMemo(() => {
      if (!userDetails) return false;
      return (
        hubblConnect.userCreator?.id === userDetails.id ||
        hubblConnect.userAcceptor?.id === userDetails.id
      );
    }, [hubblConnect.userCreator, hubblConnect.userAcceptor, userDetails]);

    const isUserDeclined = useMemo(() => {
      if (!userDetails) return false;
      return hubblConnect.declinedUserIds?.includes(userDetails.id) || false;
    }, [userDetails, hubblConnect.declinedUserIds]);

    const isConnectPassed = useMemo(() => {
      return moment(
        hubblConnect.endDateTime?.toDate() ||
          hubblConnect.startDateTime.toDate(),
      ).isBefore(moment());
    }, [hubblConnect.endDateTime, hubblConnect.startDateTime]);

    const isInvitation = useMemo(() => {
      return isUserInvited(hubblConnect.invitedUsers);
    }, [hubblConnect.invitedUsers]);

    const onAcceptBreak = useCallback(async () => {
      if (!userDetails) return;

      dispatch(GlobalActions.setIsBusy(true));

      try {
        await dispatch(
          acceptBreak({
            hubblId: hubblConnect.id,
            user: userDetails,
          }),
        ).unwrap();
      } catch (error) {
        console.error('Error accepting break:', error);
      } finally {
        dispatch(GlobalActions.setIsBusy(false));
      }
    }, [dispatch, hubblConnect.id, userDetails]);

    const onDeclineBreak = useCallback(async () => {
      if (!userDetails) return;

      dispatch(GlobalActions.setIsBusy(true));

      try {
        await dispatch(
          declineBreak({
            hubblId: hubblConnect.id,
            user: userDetails,
          }),
        ).unwrap();
      } catch (error) {
        console.error('Error declining break:', error);
      } finally {
        dispatch(GlobalActions.setIsBusy(false));
      }
    }, [dispatch, hubblConnect.id, userDetails]);

    const onUndeclineBreak = useCallback(async () => {
      if (!userDetails) return;

      dispatch(GlobalActions.setIsBusy(true));

      try {
        await dispatch(
          undeclineBreak({
            hubblId: hubblConnect.id,
            user: userDetails,
          }),
        ).unwrap();
      } catch (error) {
        console.error('Error undoing decline for break:', error);
      } finally {
        dispatch(GlobalActions.setIsBusy(false));
      }
    }, [dispatch, hubblConnect.id, userDetails]);

    const handleDeclineClick = useCallback(() => {
      setIsDeclineModalOpen(true);
    }, []);

    const handleDeclineConfirm = useCallback(async () => {
      setIsDeclineModalOpen(false);
      await onDeclineBreak();
    }, [onDeclineBreak]);

    const handleDeclineCancel = useCallback(() => {
      setIsDeclineModalOpen(false);
    }, []);

    const handleCardClick = useCallback(() => {
      navigate(`/employee/connect/${hubblConnect.id}`);
    }, [navigate, hubblConnect.id]);

    // Get participants for display (creator + acceptor if exists)
    const participants = useMemo(() => {
      const users = [];
      if (hubblConnect.userCreator) users.push(hubblConnect.userCreator);
      if (hubblConnect.userAcceptor) users.push(hubblConnect.userAcceptor);
      return users.filter((user): user is NonNullable<typeof user> =>
        Boolean(user),
      );
    }, [hubblConnect.userCreator, hubblConnect.userAcceptor]);

    // Get emoji and activity name from activities, default to handshake if none
    const connectEmoji = useMemo(() => {
      if (hubblConnect.activities && hubblConnect.activities.length > 0) {
        return hubblConnect.activities[0].emoji || '👋';
      }
      return '👋';
    }, [hubblConnect.activities]);

    const activityName = useMemo(() => {
      if (hubblConnect.activities && hubblConnect.activities.length > 0) {
        return (
          hubblConnect.activities[0].label[currentLanguage] ||
          t.connects.oneOnOneConnect
        );
      }
      return t.connects.oneOnOneConnect;
    }, [hubblConnect.activities]);

    return (
      <>
        <CardContainer
          onClick={handleCardClick}
          isPast={isPast || isUserDeclined}
          isDeclined={isUserDeclined}
          variant="connect"
          compressed={true}>
          <ConnectIcon compressed={true} emoji={connectEmoji}>
            <div style={{position: 'relative', zIndex: 199}}>
              {connectEmoji}
            </div>
            {isInvitation && (
              <div
                style={{
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  zIndex: 200,
                }}>
                <TextTicker />
              </div>
            )}
          </ConnectIcon>

          <CardContent>
            <CardTitle compressed={true}>
              <TitleWithAvatar>
                {isUserCreator && hubblConnect.userAcceptor?.firstName ? (
                  <>
                    <ClickableName
                      onClick={e => {
                        e.stopPropagation();
                        navigate(
                          `/employee/user/${hubblConnect.userAcceptor?.id}`,
                        );
                      }}>
                      <span>{hubblConnect.userAcceptor.firstName}</span>
                      <AttendeeAvatar
                        isFirst
                        attendee={hubblConnect.userAcceptor}
                      />
                    </ClickableName>
                    <span>{t.connects.acceptedYour}</span>
                    <span>{activityName}</span>
                  </>
                ) : (
                  <>
                    <span>{activityName}</span>
                    {isUserCreator ? (
                      <>
                        <span>{t.connects.youCreated}</span>
                        {hubblConnect.userAcceptor?.firstName ? (
                          <>
                            <span>{t.connects.with}</span>
                            <ClickableName
                              onClick={e => {
                                e.stopPropagation();
                                navigate(
                                  `/employee/user/${hubblConnect.userAcceptor?.id}`,
                                );
                              }}>
                              <span>{hubblConnect.userAcceptor.firstName}</span>
                              <AttendeeAvatar
                                isFirst
                                attendee={hubblConnect.userAcceptor}
                              />
                            </ClickableName>
                          </>
                        ) : (
                          <span style={{color: '#6b7280', fontStyle: 'italic'}}>
                            <AnimatedWaitingDots
                              text={t.connects.waitingForSomeone}
                            />
                          </span>
                        )}
                      </>
                    ) : (
                      hubblConnect.userCreator?.firstName && (
                        <>
                          <span>{t.connects.with}</span>
                          <ClickableName
                            onClick={e => {
                              e.stopPropagation();
                              navigate(
                                `/employee/user/${hubblConnect.userCreator?.id}`,
                              );
                            }}>
                            <span>{hubblConnect.userCreator.firstName}</span>
                            <AttendeeAvatar
                              isFirst
                              attendee={hubblConnect.userCreator}
                            />
                          </ClickableName>
                        </>
                      )
                    )}
                  </>
                )}
              </TitleWithAvatar>
            </CardTitle>

            <CardDetails>
              <TextHeading8
                color={Palette.almostBlack750}
                text={`📅 ${formatEventDate(hubblConnect.startDateTime, currentLanguage)}`}
              />
              <TextHeading8
                color={Palette.almostBlack750}
                text={`🕐 ${formatEventTime(hubblConnect.startDateTime)} ${hubblConnect.endDateTime ? `- ${formatEventTime(hubblConnect.endDateTime)}` : ''}`}
              />
            </CardDetails>

            <CardFooter>
              <div></div>

              <CardStatusIndicator>
                {/* Show decline option for initial state - only if not creator */}
                {!isUserCreator &&
                  !isUserPartOfConnect &&
                  !isUserDeclined &&
                  !isConnectPassed && (
                    <SecondaryButton
                      isCompact
                      customWidth={80}
                      label={t.events.declineConnect}
                      onClick={(e: React.MouseEvent) => {
                        e.stopPropagation();
                        handleDeclineClick();
                      }}
                    />
                  )}

                <StatusBadge
                  status={
                    isUserCreator
                      ? 'attending'
                      : isUserPartOfConnect
                        ? 'attending'
                        : isUserDeclined
                          ? 'declined'
                          : 'none'
                  }
                  onClick={(e: React.MouseEvent) => {
                    e.stopPropagation();
                    if (isUserDeclined) {
                      onUndeclineBreak();
                    } else if (isUserCreator) {
                      // Creator managing connect - navigate to details scene
                      handleCardClick();
                    } else if (isUserPartOfConnect) {
                      // User is part of connect - navigate to details scene
                      handleCardClick();
                    } else if (isConnectPassed) {
                      // Connect has passed - navigate to details scene
                      handleCardClick();
                    } else {
                      onAcceptBreak();
                    }
                  }}
                  isClickable={true}>
                  {isUserCreator ? (
                    hubblConnect.userAcceptor ? (
                      t.connects.connectedViewDetails
                    ) : (
                      <AnimatedWaitingDots
                        text={t.connects.waitingForSomeone}
                      />
                    )
                  ) : isUserPartOfConnect ? (
                    t.connects.connectedViewDetails
                  ) : isUserDeclined ? (
                    t.connects.declinedClickToChange
                  ) : isConnectPassed ? (
                    t.connects.completedViewDetails
                  ) : (
                    t.connects.clickToAccept
                  )}
                </StatusBadge>
              </CardStatusIndicator>
            </CardFooter>
          </CardContent>
          <DevToolFloatingId id={hubblConnect.id} />
        </CardContainer>

        <ConfirmationModal
          isOpen={isDeclineModalOpen}
          onClose={handleDeclineCancel}
          onConfirm={handleDeclineConfirm}
          title={t.events.declineConnect}
          message={t.events.declineConnectMessage}
          confirmLabel={t.events.declineConnect}
          cancelLabel={t.events.keepInvitation}
        />
      </>
    );
  },
);

const ConnectIcon = styled.div<{compressed?: boolean; emoji?: string}>`
  height: ${props => (props.compressed ? '130px' : '200px')};
  width: 200px;
  object-fit: cover;
  flex-shrink: 0;
  z-index: 3;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 12px;
  background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  font-size: ${props => (props.compressed ? '2.5rem' : '4rem')};
  position: relative;
  overflow: hidden;

  /* Emoji pattern overlay */
  &::after {
    content: '';
    position: absolute;
    top: -12px;
    left: -20px;
    right: 0;
    bottom: 0;
    background-image: ${props =>
      props.emoji
        ? `url("data:image/svg+xml,${encodeURIComponent(`
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 40 40">
              <text x="20" y="25" font-size="20" text-anchor="middle" fill="rgba(255,255,255,0.3)">${props.emoji}</text>
            </svg>
          `)}")`
        : 'none'};
    background-size: 40px 40px;
    background-repeat: repeat;
    pointer-events: none;
    z-index: 1;
  }

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    height: 120px;
    width: 100%;
    aspect-ratio: 16/9;
    font-size: 3rem;

    &::after {
      background-size: 30px 30px;
    }
  }
`;
